import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Eye, EyeOff, ChevronLeft } from 'lucide-react';
import { CalendarRange, Shield, Layers } from 'lucide-react';
import { GabryxFooter, LegalFooterLinks } from '../components/GabryxBranding';
import { PilotSlotsBadge } from '../components/PilotSlotsBadge';

const PROFILES = [
  {
    id: 'opc',
    label: 'OPC',
    subtitle: 'Pilotage de chantier',
    description: 'Planning, Gantt, rapports hebdo/mensuels, documents de chantier',
    icon: CalendarRange,
    color: 'border-[#0055FF]',
    bgColor: 'bg-[#0055FF]/5',
    iconColor: 'text-[#0055FF]',
  },
  {
    id: 'csps',
    label: 'CSPS',
    subtitle: 'Coordination sécurité',
    description: 'PGC, PPSPS, DIUO, inspections terrain, visites',
    icon: Shield,
    color: 'border-[#FF6B00]',
    bgColor: 'bg-[#FF6B00]/5',
    iconColor: 'text-[#FF6B00]',
  },
  {
    id: 'opc_csps',
    label: 'OPC + CSPS',
    subtitle: 'Accès complet',
    description: 'Tous les modules OPC et CSPS reunis',
    icon: Layers,
    color: 'border-emerald-500',
    bgColor: 'bg-emerald-500/5',
    iconColor: 'text-emerald-500',
  },
];

export function RegisterPage() {
  const [step, setStep] = useState(1);
  const [profileType, setProfileType] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedCGU, setAcceptedCGU] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [legalDocs, setLegalDocs] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pilotFull, setPilotFull] = useState(false);

  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Charge les documents juridiques courants (CGU + Confidentialite) pour
  // afficher la bonne version dans les cases et enregistrer une preuve
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    axios.get(`${API}/legal/current`)
      .then(r => {
        const byType = {};
        (r.data.documents || []).forEach(d => { byType[d.doc_type] = d; });
        setLegalDocs(byType);
      })
      .catch(() => setLegalDocs({}));
  }, []);

  // Detect if pilot phase is full -> redirect to Stripe checkout after register
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    axios.get(`${API}/pilot-mode/public`)
      .then(r => {
        const d = r.data || {};
        // Pilot is "full" when enabled & 0 places left, OR phase has ended
        setPilotFull(d.enabled ? (d.places_remaining <= 0) : true);
      })
      .catch(() => setPilotFull(false));
  }, []);

  const handleSelectProfile = (id) => {
    setProfileType(id);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (!acceptedCGU) {
      setError('Vous devez accepter les Conditions Générales d\'Utilisation pour continuer');
      return;
    }
    if (!acceptedPrivacy) {
      setError('Vous devez confirmer avoir lu la Politique de confidentialité pour continuer');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name, profileType || 'opc_csps');

    if (result.success) {
      // Enregistre la preuve d'acceptation (CGU + Confidentialite) côté serveur
      // Le serveur capture IP + User-Agent lui-meme (elements techniques de preuve).
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        if (legalDocs.cgu) {
          await axios.post(`${API}/legal/accept`, {
            doc_id: legalDocs.cgu.id, source: 'signup',
            checkbox_label: `J'ai lu et j'accepte les CGU v${legalDocs.cgu.version}`,
          }, { withCredentials: true });
        }
        if (legalDocs.privacy) {
          await axios.post(`${API}/legal/accept`, {
            doc_id: legalDocs.privacy.id, source: 'signup',
            checkbox_label: `J'ai lu la Politique de confidentialité v${legalDocs.privacy.version}`,
          }, { withCredentials: true });
        }
      } catch (_) { /* non-bloquant */ }

      // Si phase pilote complete/terminee -> Stripe checkout obligatoire
      if (pilotFull) {
        navigate('/subscription?from=register&pilot_full=1', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1697305592218-d5d0c1bab2e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbWFuYWdlciUyMHRhYmxldHxlbnwwfHx8fDE3NzUxNjEzMjZ8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-[#FF6B00]/80 flex items-center justify-center">
          <div className="text-center text-white p-8 max-w-md">
            <h2 className="text-4xl font-bold mb-4">Rejoignez CHANTIX</h2>
            <p className="text-lg opacity-90">
              La solution moderne pour les professionnels OPC et CSPS du BTP.
            </p>
          </div>
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img 
              src="/chantix-logo.png" 
              alt="CHANTIX" 
              className="h-20 w-auto object-contain mb-4"
              data-testid="register-logo"
            />
            {step === 1 ? (
              <>
                <h1 className="text-2xl font-bold text-[#111827] mb-2" data-testid="register-title">Choisissez votre profil</h1>
                <p className="text-sm text-[#4B5563]">Sélectionnez le module adapté à votre activité</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setStep(1)} className="text-[#4B5563] hover:text-[#111827]" data-testid="back-to-profiles">
                    <ChevronLeft size={20} />
                  </button>
                  <h1 className="text-2xl font-bold text-[#111827]" data-testid="register-title">Créer un compte</h1>
                </div>
                <p className="text-sm text-[#4B5563]">
                  Profil : <span className="font-medium text-[#111827]">{PROFILES.find(p => p.id === profileType)?.label}</span>
                </p>
              </>
            )}
          </div>

          {step === 1 && (
            <div className="space-y-3" data-testid="profile-selection">
              <PilotSlotsBadge className="mb-2" />
              {PROFILES.map(p => (
                <Card key={p.id}
                  className={`cursor-pointer border-2 hover:shadow-md transition-all ${profileType === p.id ? p.color + ' shadow-md' : 'border-transparent hover:border-gray-200'}`}
                  onClick={() => handleSelectProfile(p.id)}
                  data-testid={`profile-${p.id}`}
                >
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className={`w-12 h-12 rounded-xl ${p.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <p.icon size={24} className={p.iconColor} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#111827]">{p.label}</span>
                        <span className="text-xs text-[#6B7280]">{p.subtitle}</span>
                      </div>
                      <p className="text-xs text-[#6B7280] mt-0.5">{p.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="my-6 flex items-center">
                <div className="flex-1 border-t border-[#E5E7EB]"></div>
                <span className="px-4 text-sm text-[#4B5563]">ou</span>
                <div className="flex-1 border-t border-[#E5E7EB]"></div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={loginWithGoogle}
                data-testid="google-register-btn"
                className="w-full border-2 border-[#111827] text-[#111827] hover:bg-gray-100"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                S'inscrire avec Google
              </Button>

              <p className="mt-6 text-center text-sm text-[#4B5563]">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-[#0055FF] font-medium hover:underline" data-testid="login-link">
                  Se connecter
                </Link>
              </p>
            </div>
          )}

          {step === 2 && (
            <>
              {pilotFull && (
                <div className="mb-4 p-4 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="pilot-full-notice">
                  <p className="font-semibold mb-1">⚠️ Phase pilote complète</p>
                  <p className="text-xs leading-relaxed">Les 10 places gratuites ont été attribuées. Vous pouvez créer votre compte, puis souscrire à un abonnement CHANTIX (à partir de 49&nbsp;€/mois) pour accéder à la plateforme.</p>
                </div>
              )}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom complet</Label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Jean Dupont" required data-testid="register-name-input" className="mt-1" />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com" required data-testid="register-email-input" className="mt-1" />
                </div>

                <div>
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum"
                      required data-testid="register-password-input" className="pr-10" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#111827]">
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <Input id="confirmPassword" type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repetez le mot de passe"
                    required data-testid="register-confirm-password-input" className="mt-1" />
                </div>

                <div className="space-y-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="acceptCGU"
                      checked={acceptedCGU}
                      onChange={(e) => setAcceptedCGU(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-[#0055FF] focus:ring-[#0055FF] cursor-pointer"
                      data-testid="register-accept-cgu"
                    />
                    <label htmlFor="acceptCGU" className="text-xs text-[#4B5563] leading-relaxed cursor-pointer">
                      J&apos;ai lu et j&apos;accepte les{' '}
                      <Link to="/cgu" target="_blank" className="text-[#0055FF] font-medium hover:underline">
                        Conditions Générales d&apos;Utilisation
                      </Link>
                      {legalDocs.cgu && <span className="text-gray-400"> (v{legalDocs.cgu.version})</span>}
                    </label>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="acceptPrivacy"
                      checked={acceptedPrivacy}
                      onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-[#0055FF] focus:ring-[#0055FF] cursor-pointer"
                      data-testid="register-accept-privacy"
                    />
                    <label htmlFor="acceptPrivacy" className="text-xs text-[#4B5563] leading-relaxed cursor-pointer">
                      J&apos;ai pris connaissance de la{' '}
                      <Link to="/confidentialite" target="_blank" className="text-[#0055FF] font-medium hover:underline">
                        Politique de confidentialité
                      </Link>
                      {legalDocs.privacy && <span className="text-gray-400"> (v{legalDocs.privacy.version})</span>}
                    </label>
                  </div>
                </div>

                <Button type="submit" disabled={loading || !acceptedCGU || !acceptedPrivacy} data-testid="register-submit-btn"
                  className="w-full bg-[#0055FF] hover:bg-blue-700">
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creation...</>
                  ) : (
                    'Créer mon compte'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-[#4B5563]">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-[#0055FF] font-medium hover:underline" data-testid="login-link">
                  Se connecter
                </Link>
              </p>
              <div className="mt-4">
                <LegalFooterLinks variant="light" />
                <p className="text-center text-[10px] text-gray-400 mt-2">&copy; {new Date().getFullYear()} GABRYX — CHANTIX</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
